import React, { useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { InsightsStatus, JobName, JobStatus } from './types';

interface Props {
  status: InsightsStatus | null;
  onStart: (job: JobName, limit?: number) => void;
  starting: JobName | null;
}

// 'daily' is the scheduled run: it has no button, but while it runs the
// buttons are disabled, so its line says why.
type JobLineName = JobName | 'daily';
const JOB_LABELS: Record<JobLineName, string> = {
  observe: 'Tagging creatives',
  draft: 'Drafting tag lists',
  map: 'Applying approved lists',
  daily: 'Daily tagging run',
};

const ACTIVE_STATES = new Set(['active', 'waiting', 'delayed']);

export function isRunning(job?: JobStatus) {
  return Boolean(job && ACTIVE_STATES.has(job.state));
}

function JobLine({ name, job }: { name: JobLineName; job?: JobStatus }) {
  if (!job) return null;
  const running = isRunning(job);
  const progress =
    job.progress && job.progress.total
      ? ` ${job.progress.done} of ${job.progress.total}`
      : '';
  let text: string;
  if (running) text = `${JOB_LABELS[name]}…${progress}`;
  else if (job.state === 'failed')
    text = `${JOB_LABELS[name]} failed: ${job.error}`;
  else if (job.finishedAt)
    text = `${JOB_LABELS[name]}: finished ${new Date(job.finishedAt).toLocaleString()}`;
  else return null;
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        color: job.state === 'failed' ? '#DC2626' : '#64748B',
      }}
    >
      {text}
    </Typography>
  );
}

// The top strip of the Tags tab: how far tagging has got, what it has cost, and
// the three buttons that queue work in the background worker.
export default function TaggingStatus({ status, onStart, starting }: Props) {
  const [batch, setBatch] = useState(100);
  if (!status) return null;
  const pct = status.creatives ? (status.observed / status.creatives) * 100 : 0;
  const anyRunning = Object.values(status.jobs).some(isRunning);
  const remaining = Math.max(0, status.creatives - status.observed);
  const batchOptions = [25, 100, status.observeCapPerRun].filter(
    (n, i, all) => all.indexOf(n) === i,
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {status.observed.toLocaleString()} of{' '}
          {status.creatives.toLocaleString()} creatives tagged
        </Typography>
        {status.failed > 0 && (
          <Typography variant="body2" sx={{ color: '#D97706' }}>
            {status.failed} couldn't be read (tried again after a week)
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Model spend on tagging so far. Video calls go through a provider that doesn't report a price, so they're counted separately.">
          <Typography
            variant="body2"
            sx={{ color: '#64748B', fontVariantNumeric: 'tabular-nums' }}
          >
            AI cost so far:{' '}
            {status.costUsd == null ? 'n/a' : `$${status.costUsd.toFixed(2)}`}
            {status.unpricedCalls > 0
              ? ` + ${status.unpricedCalls} video calls (unpriced)`
              : ''}
          </Typography>
        </Tooltip>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ my: 1.5, height: 6, borderRadius: 3 }}
      />

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          select
          size="small"
          label="Batch"
          value={batch}
          onChange={e => setBatch(Number(e.target.value))}
          sx={{ width: 110 }}
        >
          {batchOptions.map(n => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Has the AI watch or look at the next highest-spending untagged creatives. About $0.01 per image and a few cents per video.">
          <span>
            <Button
              variant="contained"
              disabled={anyRunning || starting !== null || remaining === 0}
              onClick={() => onStart('observe', batch)}
            >
              Tag next {Math.min(batch, remaining)} creatives
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Reads everything tagged so far and proposes persona and selling-point values for you to approve below. Never changes approved values.">
          <span>
            <Button
              variant="outlined"
              disabled={
                anyRunning || starting !== null || status.observed === 0
              }
              onClick={() => onStart('draft')}
            >
              Draft / suggest list values
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Matches every tagged creative to the approved lists. Runs automatically every day; use this after approving or editing values to see the effect now.">
          <span>
            <Button
              variant="outlined"
              disabled={anyRunning || starting !== null}
              onClick={() => onStart('map')}
            >
              Apply approved lists
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        {(['daily', 'observe', 'draft', 'map'] as JobLineName[]).map(name => (
          <JobLine key={name} name={name} job={status.jobs[name]} />
        ))}
      </Box>
    </Paper>
  );
}
